/** Badge di stato con i colori del mockup (pill p-ok / p-warn / p-bad / p-crit / p-info / p-mute). */
import { Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { ratingOf, type ActionDto, type AuditDto, type MonitoringOff } from "@audit-plan/shared";
import { palette } from "../theme";

type Tone = "ok" | "warn" | "bad" | "crit" | "info" | "mute";
const tones: Record<Tone, [string, string]> = {
  ok: [palette.ok, palette.okSoft],
  warn: [palette.warn, palette.warnSoft],
  bad: [palette.bad, palette.badSoft],
  crit: [palette.crit, palette.critSoft],
  info: [palette.primary, palette.primarySoft],
  mute: [palette.muted, palette.sunk],
};
export function Pill({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  const [fg, bg] = tones[tone];
  return (
    <Tag bordered={false} style={{ color: fg, background: bg, fontWeight: 600, borderRadius: 999 }}>
      {children}
    </Tag>
  );
}

const auditTone: Record<string, Tone> = { planned: "mute", confirmed: "info", in_progress: "warn", completed: "info", in_validation: "warn", closed: "ok" };
export function AuditStatusPill({ a }: { a: Pick<AuditDto, "status" | "outcome"> }) {
  const { t } = useTranslation();
  if (a.outcome === "phaseout") return <Pill tone="crit">{t("phaseout")}</Pill>;
  return <Pill tone={auditTone[a.status] ?? "mute"}>{t(`st_${a.status}`)}</Pill>;
}

/** Esito senza punteggio degli audit storici, dal registro (RAT26): positivo / negativo. */
export function OutcomePill({ outcome }: { outcome: AuditDto["outcome"] }) {
  const { t } = useTranslation();
  if (outcome === "pass") return <Pill tone="ok">{t("outcome_pass")}</Pill>;
  if (outcome === "fail") return <Pill tone="bad">{t("outcome_fail")}</Pill>;
  return null;
}

const actTone: Record<string, Tone> = { open: "mute", progress: "warn", verify: "info", done: "ok", closed: "ok" };
export function ActionStatusPill({ a }: { a: Pick<ActionDto, "status" | "overdue"> }) {
  const { t, i18n } = useTranslation();
  return (
    <Pill tone={a.overdue ? "bad" : (actTone[a.status] ?? "mute")}>
      {t(`as_${a.status}`)}
      {a.overdue ? ` · ${i18n.language === "en" ? "overdue" : "scaduta"}` : ""}
    </Pill>
  );
}

const sevTone: Record<string, Tone> = { obs: "info", minor: "warn", major: "bad", critical: "crit", zero: "crit" };
export const SeverityPill = ({ s }: { s: string }) => {
  const { t } = useTranslation();
  return <Pill tone={sevTone[s] ?? "mute"}>{t(`sev_${s}`)}</Pill>;
};
const riskTone: Record<string, Tone> = { low: "ok", medium: "warn", high: "bad" };
export const RiskPill = ({ r }: { r: string }) => {
  const { t } = useTranslation();
  return <Pill tone={riskTone[r] ?? "mute"}>{t(`risk_${r}`)}</Pill>;
};
const prTone: Record<string, Tone> = { low: "mute", medium: "warn", high: "bad" };
export const PriorityPill = ({ p }: { p: string }) => {
  const { t } = useTranslation();
  return <Pill tone={prTone[p] ?? "mute"}>{t(`pr_${p}`)}</Pill>;
};
const evTone: Record<string, Tone> = { pending: "warn", approved: "ok", rejected: "bad" };
export const EvidenceStatusPill = ({ s }: { s: string | null }) => {
  const { t } = useTranslation();
  return s ? <Pill tone={evTone[s] ?? "mute"}>{t(`ev_${s}`)}</Pill> : null;
};

const ratingColor: Record<string, string> = { A: palette.ok, B: palette.primary, C: palette.warn, D: palette.bad, NQ: palette.heading, "-": palette.muted };
/** override "D": regola del rating (NC critical / zero tolerance) che forza la classe D; reason compare nel tooltip. */
export function Rating({ score, override, reason }: { score: number | null | undefined; override?: string | null; reason?: string | null }) {
  const { t } = useTranslation();
  const r = ratingOf(score, override);
  return (
    <Tooltip title={[score == null ? "" : score.toFixed(1), override === "D" ? (reason ?? "") : "", r === "NQ" ? t("rating_nq") : ""].filter(Boolean).join(" · ")}>
      <span style={{ display: "inline-grid", placeItems: "center", minWidth: 26, padding: r === "NQ" ? "0 5px" : 0, height: 26, borderRadius: 7, fontWeight: 800, color: "#fff", background: ratingColor[r] }}>
        {r === "-" ? "—" : r}
      </span>
    </Tooltip>
  );
}

/** Segno «a mano» accanto a rating o classe di rischio impostati dall'admin (AUD-F2.20), motivazione nel tooltip. */
export function ManualMark({ note }: { note?: string | null }) {
  const { t } = useTranslation();
  return (
    <Tooltip title={note || undefined}>
      <Tag color="purple" style={{ marginInlineEnd: 0 }}>{t("m_manual")}</Tag>
    </Tooltip>
  );
}

export const fmtD = (s?: string | null) => (s ? dayjs(s).format("DD/MM/YYYY") : "—");
export const fmtDT = (s?: string | null) => (s ? dayjs(s).format("DD/MM/YYYY HH:mm") : "—");
export const fmtN = (n?: number | null, dec = 0) => (n == null || Number.isNaN(n) ? "—" : n.toLocaleString("it-IT", { minimumFractionDigits: dec, maximumFractionDigits: dec }));

/** Data pianificata; per i semi-annunciati la finestra «01/11 – 15/11/2026» (AUD-F8.4). */
export const fmtWhen = (a: { plannedDate: string | null; plannedDateTo?: string | null }) =>
  !a.plannedDate ? "—" : a.plannedDateTo ? `${dayjs(a.plannedDate).format(dayjs(a.plannedDate).year() === dayjs(a.plannedDateTo).year() ? "DD/MM" : "DD/MM/YYYY")} – ${fmtD(a.plannedDateTo)}` : fmtD(a.plannedDate);

/** Monitoraggio delle NC sospeso (fornitore o stabilimento chiuso o non attivo, AUD-F8.4). */
export function MonitoringOffPill({ off }: { off?: MonitoringOff | null }) {
  const { t } = useTranslation();
  if (!off) return null;
  return (
    <Tooltip title={t(`mon_${off}`)}>
      <span>
        <Pill tone="mute">{t("mon_off")}</Pill>
      </span>
    </Tooltip>
  );
}

/** Scadenza con etichetta "scaduta da N giorni" / "oggi" / "tra N giorni" (<=7); con il monitoraggio sospeso niente scadenza. */
export function DueLabel({ a }: { a: Pick<ActionDto, "dueDate" | "status"> & { monitoringOff?: MonitoringOff | null } }) {
  const { t } = useTranslation();
  if (!a.dueDate) return <>—</>;
  if (a.status === "done" || a.status === "closed") return <>{fmtD(a.dueDate)}</>;
  if (a.monitoringOff)
    return (
      <span>
        {fmtD(a.dueDate)} <MonitoringOffPill off={a.monitoringOff} />
      </span>
    );
  const d = dayjs(a.dueDate).startOf("day").diff(dayjs().startOf("day"), "day");
  return (
    <span>
      {fmtD(a.dueDate)}{" "}
      {d < 0 ? (
        <Pill tone="bad">{`${t("overdueBy")} ${-d} ${t("days")}`}</Pill>
      ) : d === 0 ? (
        <Pill tone="warn">{t("today")}</Pill>
      ) : d <= 7 ? (
        <Pill tone="warn">{`${t("dueIn")} ${d} ${t("days")}`}</Pill>
      ) : null}
    </span>
  );
}
